import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getLocalApiUrl, authFetch } from '../../config/api'
import StaffPhoto from '../../components/StaffPhoto'
import EcddAmlPepPanel from '../compliance-safe/components/EcddAmlPepPanel'
import EmployeeKycWizardModal from '../../components/kyc/EmployeeKycWizardModal'
import { staffWorkAreaLabel } from '../../constants/staffWorkArea'
import { formatSydneyDate, formatSydneyDateTime } from '../../utils/sydneyDateTime'
import {
  UserCircleIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  CalendarIcon,
  IdentificationIcon,
  FlagIcon,
  ArrowUpTrayIcon,
  ChevronLeftIcon
} from '@heroicons/react/24/outline'

const STAFF_DOC_TYPES = [
  { value: 'self_declaration', label: 'Self-declaration' },
  { value: 'pep_declaration', label: 'PEP / conflict declaration' },
  { value: 'kyc', label: 'KYC / identity' },
  { value: 'certification', label: 'Certification / training record' },
  { value: 'general', label: 'General' },
]

function formatStaffDocType(t) {
  const row = STAFF_DOC_TYPES.find((x) => x.value === t)
  return row ? row.label : (t || 'General').replace(/_/g, ' ')
}

export default function EmployeeDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const fileRef = useRef(null)
  const [loading, setLoading] = useState(true)
  const [employee, setEmployee] = useState(null)
  const [kycStatus, setKycStatus] = useState(null)
  const [amlStatus, setAmlStatus] = useState(null)
  const [kycOverview, setKycOverview] = useState(null)
  const [photoKey, setPhotoKey] = useState(0)
  const [photoSource, setPhotoSource] = useState(null)
  const [photoBusy, setPhotoBusy] = useState(false)
  const photoInputRef = useRef(null)
  const [activeTab, setActiveTab] = useState('overview') // overview, kyc, aml, documents
  const [staffDocuments, setStaffDocuments] = useState([])
  const [staffDocsLoading, setStaffDocsLoading] = useState(false)
  const [docUploadType, setDocUploadType] = useState('self_declaration')
  const [docError, setDocError] = useState(null)
  const [docUploading, setDocUploading] = useState(false)
  const [kycWizardOpen, setKycWizardOpen] = useState(false)
  const [deferOpen, setDeferOpen] = useState(false)
  const [deferReason, setDeferReason] = useState('')
  const [deferLoading, setDeferLoading] = useState(false)
  const [actionError, setActionError] = useState(null)

  const loadStaffDocuments = useCallback(async () => {
    if (!id) return
    setStaffDocsLoading(true)
    setDocError(null)
    try {
      const r = await authFetch(getLocalApiUrl(`/api/staff/${id}/documents`))
      const j = await r.json()
      if (!r.ok || !j.success) throw new Error(j.error || 'Failed to load staff documents')
      setStaffDocuments(Array.isArray(j.data) ? j.data : [])
    } catch (e) {
      setStaffDocuments([])
      setDocError(e.message || 'Failed to load documents')
    } finally {
      setStaffDocsLoading(false)
    }
  }, [id])

  const refreshAmlStatus = useCallback(async () => {
    try {
      const [ar, or] = await Promise.all([
        authFetch(getLocalApiUrl(`/api/staff/${id}/aml-status`)),
        authFetch(getLocalApiUrl(`/api/staff/${id}/kyc-overview`)),
      ])
      const aj = await ar.json()
      const oj = await or.json()
      if (aj.success) setAmlStatus(aj.data)
      if (oj.success) setKycOverview(oj.data)
    } catch { /* keep previous */ }
  }, [id])

  const loadEmployeeDetails = useCallback(async () => {
    setLoading(true)
    setDocError(null)
    try {
      const empResponse = await authFetch(getLocalApiUrl(`/api/staff/${id}`))
      const empData = await empResponse.json()

      if (empData.success) {
        setEmployee(empData.data)

        const [kycResponse, amlResponse] = await Promise.all([
          authFetch(getLocalApiUrl(`/api/staff/${id}/kyc-status`)),
          authFetch(getLocalApiUrl(`/api/staff/${id}/aml-status`)),
        ])
        const kycData = await kycResponse.json()
        const amlData = await amlResponse.json()
        if (kycData.success) setKycStatus(kycData.data)
        if (amlData.success) setAmlStatus(amlData.data)
        try {
          const ov = await (await authFetch(getLocalApiUrl(`/api/staff/${id}/kyc-overview`))).json()
          if (ov.success) setKycOverview(ov.data)
        } catch { /* optional */ }

        await loadStaffDocuments()
      } else {
        setEmployee(null)
        setKycStatus(null)
        setAmlStatus(null)
        setStaffDocuments([])
      }
    } catch (error) {
      console.error('Error loading employee details:', error)
      setEmployee(null)
    } finally {
      setLoading(false)
    }
  }, [id, loadStaffDocuments])

  useEffect(() => {
    loadEmployeeDetails()
  }, [loadEmployeeDetails])

  const uploadPhoto = async (file) => {
    if (!file) return
    setPhotoBusy(true)
    setActionError(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await authFetch(getLocalApiUrl(`/api/staff/${id}/photo`), { method: 'POST', body: fd })
      const j = await res.json().catch(() => ({}))
      if (!res.ok || !j.success) throw new Error(j.error || `Upload failed (${res.status})`)
      setPhotoKey((k) => k + 1)
    } catch (e) {
      setActionError(e.message || 'Photo upload failed')
    } finally {
      setPhotoBusy(false)
    }
  }

  const removePhoto = async () => {
    if (!window.confirm('Remove the uploaded photo? If KYC is complete, the KYC selfie will be used instead.')) return
    setPhotoBusy(true)
    setActionError(null)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/staff/${id}/photo`), { method: 'DELETE' })
      const j = await res.json().catch(() => ({}))
      if (!res.ok || !j.success) throw new Error(j.error || `Remove failed (${res.status})`)
      setPhotoKey((k) => k + 1)
    } catch (e) {
      setActionError(e.message || 'Could not remove photo')
    } finally {
      setPhotoBusy(false)
    }
  }

  const fetchStaffDocBlob = async (docId) => {
    const url = getLocalApiUrl(`/api/staff/${id}/documents/${encodeURIComponent(String(docId))}/download`)
    const res = await authFetch(url)
    if (!res.ok) {
      const j = await res.json().catch(() => ({}))
      throw new Error(j.error || `Download failed (${res.status})`)
    }
    return res.blob()
  }

  // View: opens in a new tab. The tab is opened first so the browser doesn't block it as a popup.
  const viewStaffDocument = async (docId, fileName) => {
    const w = window.open('', '_blank')
    try {
      setDocError(null)
      const objectUrl = URL.createObjectURL(await fetchStaffDocBlob(docId))
      if (w) {
        w.location.href = objectUrl
      } else {
        const a = document.createElement('a')
        a.href = objectUrl
        a.download = fileName || 'document'
        a.click()
      }
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000)
    } catch (e) {
      if (w) w.close()
      setDocError(e.message || 'Could not open document')
    }
  }

  const downloadStaffDocument = async (docId, fileName) => {
    try {
      setDocError(null)
      const objectUrl = URL.createObjectURL(await fetchStaffDocBlob(docId))
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = fileName || 'document'
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(objectUrl), 8000)
    } catch (e) {
      setDocError(e.message || 'Could not download document')
    }
  }

  const uploadStaffDocument = async () => {
    const input = fileRef.current
    const file = input?.files?.[0]
    if (!file) {
      setDocError('Choose a file to upload')
      return
    }
    setDocUploading(true)
    setDocError(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const qs = new URLSearchParams({ type: docUploadType })
      const r = await authFetch(getLocalApiUrl(`/api/staff/${id}/documents?${qs}`), { method: 'POST', body: fd })
      const j = await r.json()
      if (!r.ok || !j.success) throw new Error(j.error || `Upload failed (${r.status})`)
      if (input) input.value = ''
      await loadStaffDocuments()
    } catch (e) {
      setDocError(e.message || 'Upload failed')
    } finally {
      setDocUploading(false)
    }
  }

  const openKycWizard = () => {
    setActionError(null)
    setKycWizardOpen(true)
  }

  const handleKycDefer = async () => {
    setDeferLoading(true)
    setActionError(null)
    try {
      const r = await authFetch(getLocalApiUrl(`/api/staff/${id}/kyc-defer`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: deferReason.trim() || undefined }),
      })
      const j = await r.json()
      if (!r.ok || !j.success) throw new Error(j.error || 'Could not defer KYC')
      setDeferOpen(false)
      setDeferReason('')
      await loadEmployeeDetails()
    } catch (e) {
      setActionError(e.message || 'Defer failed')
    } finally {
      setDeferLoading(false)
    }
  }

  const kycNeedsAction = () => {
    const s = kycStatus?.status
    return !s || s === 'NOT_STARTED' || s === 'NOT_SCREENED' || s === 'DEFERRED' || s === 'IN_PROGRESS'
  }

  const kycActionLabel = () => {
    const s = kycStatus?.status
    if (s === 'IN_PROGRESS') return 'Continue KYC wizard'
    if (s === 'DEFERRED') return 'Complete KYC wizard'
    if (s === 'FAILED' || s === 'REQUIRES_REVIEW') return 'Retry KYC wizard'
    return 'Start KYC wizard'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-psg-teal mx-auto mb-4"></div>
          <p className="text-gray-600">Loading employee details...</p>
        </div>
      </div>
    )
  }

  if (!employee) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-red-600 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-red-900 mb-2">Employee Not Found</h3>
          <p className="text-red-700 mb-4">The requested employee could not be found.</p>
          <button
            onClick={() => navigate('/admin/employees')}
            className="px-4 py-2 bg-psg-teal text-white rounded-lg hover:bg-psg-teal-700"
          >
            Back to Employees
          </button>
        </div>
      </div>
    )
  }

  const getStatusBadge = (status) => {
    const badges = {
      'VERIFIED': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200', icon: CheckCircleIcon },
      'PENDING': { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200', icon: ClockIcon },
      'IN_PROGRESS': { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200', icon: ClockIcon },
      'NOT_STARTED': { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200', icon: ClockIcon },
      'DEFERRED': { bg: 'bg-amber-100', text: 'text-amber-900', border: 'border-amber-200', icon: ClockIcon },
      'NOT_SCREENED': { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200', icon: ClockIcon },
      'REQUIRES_REVIEW': { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200', icon: ExclamationTriangleIcon },
      'FAILED': { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200', icon: ExclamationTriangleIcon },
      'MATCH_FOUND': { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200', icon: FlagIcon },
      'PARTIAL': { bg: 'bg-amber-100', text: 'text-amber-900', border: 'border-amber-200', icon: ExclamationTriangleIcon },
      'CLEAR': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200', icon: CheckCircleIcon },
      'CLEAN': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200', icon: CheckCircleIcon }
    }
    const badge = badges[status] || badges['PENDING']
    const Icon = badge.icon
    
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${badge.bg} ${badge.text} ${badge.border}`}>
        <Icon className="w-4 h-4 mr-1.5" />
        {status?.replace(/_/g, ' ')}
      </span>
    )
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate('/admin/employees')}
          className="mb-2 inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
        >
          <ChevronLeftIcon className="w-4 h-4" /> Back to Employees
        </button>
        <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <StaffPhoto staffId={id} name={employee.name} size={56} reloadKey={photoKey} onLoaded={setPhotoSource} />
          <div className="text-left">
            <h1 className="text-2xl font-bold text-gray-900">{employee.name}</h1>
            <p className="text-sm text-gray-500">Employee ID: {employee.employee_id || 'Not assigned'}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {employee.active ? (
            <span className="px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm font-medium">
              Active
            </span>
          ) : (
            <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-800 text-sm font-medium">
              Inactive
            </span>
          )}
        </div>
      </div>
      </div>

      {actionError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {actionError}
        </div>
      )}

      {kycNeedsAction() && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="font-semibold text-amber-950">Identity verification required</h3>
            <p className="text-sm text-amber-900 mt-1">
              {kycStatus?.status === 'DEFERRED'
                ? 'KYC was deferred — complete the wizard when the employee is ready.'
                : kycStatus?.status === 'IN_PROGRESS'
                  ? 'KYC onboarding is in progress — continue where you left off.'
                  : 'Upload ID, run ProfileSafe OCR, and submit for DVS / AML screening.'}
            </p>
            {kycStatus?.status === 'DEFERRED' && kycStatus.defer_reason && (
              <p className="text-xs text-amber-800 mt-2">
                Reason: {kycStatus.defer_reason}
                {kycStatus.deferred_at
                  ? ` · ${formatSydneyDate(new Date(kycStatus.deferred_at))}`
                  : ''}
              </p>
            )}
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <button
              type="button"
              onClick={openKycWizard}
              className="px-4 py-2 bg-psg-teal text-white rounded-lg hover:bg-psg-teal-700 text-sm font-medium"
            >
              {kycActionLabel()}
            </button>
            {(kycStatus?.status === 'NOT_STARTED' ||
              kycStatus?.status === 'NOT_SCREENED' ||
              !kycStatus?.status) && (
              <button
                type="button"
                onClick={() => setDeferOpen(true)}
                className="px-4 py-2 border border-amber-700 text-amber-950 rounded-lg hover:bg-amber-100 text-sm font-medium"
              >
                Skip for now
              </button>
            )}
          </div>
        </div>
      )}

      {/* Status Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Basic Info Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900">Basic Information</h3>
            <UserCircleIcon className="w-6 h-6 text-gray-400" />
          </div>
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-3">
              <StaffPhoto staffId={id} name={employee.name} size={44} reloadKey={photoKey} />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                    onChange={(e) => { uploadPhoto(e.target.files?.[0]); e.target.value = '' }} />
                  <button type="button" disabled={photoBusy} onClick={() => photoInputRef.current?.click()}
                    className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-psg-teal hover:bg-gray-50 disabled:opacity-60">
                    {photoBusy ? 'Working…' : photoSource ? 'Replace photo' : 'Upload photo'}
                  </button>
                  {photoSource === 'manual' && (
                    <button type="button" disabled={photoBusy} onClick={removePhoto}
                      className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-60">Remove</button>
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {photoSource === 'kyc' ? 'Using the KYC selfie' : photoSource === 'manual' ? 'Uploaded photo' : 'JPEG, PNG or WebP, up to 5 MB'}
                </div>
              </div>
            </div>
            <div>
              <div className="text-gray-500">Designation</div>
              <div className="font-medium text-gray-900">{employee.designation || employee.role}</div>
            </div>
            <div>
              <div className="text-gray-500">Work area</div>
              <div className="font-medium text-gray-900">{staffWorkAreaLabel(employee.work_area)}</div>
            </div>
            <div>
              <div className="text-gray-500">Scope</div>
              <div className="font-medium text-gray-900 capitalize">{employee.level}</div>
            </div>
            {employee.venue_id && (
              <div>
                <div className="text-gray-500">Venue</div>
                <div className="font-medium text-gray-900">{employee.venue_id}</div>
              </div>
            )}
          </div>
        </div>

        {/* KYC Card: overall result built from Identity (DVS), AML / PEP and Adverse media */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-900">KYC</h3>
            <IdentificationIcon className="w-6 h-6 text-gray-400" />
          </div>
          {kycOverview ? (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                {getStatusBadge(kycOverview.overall)}
                <span className="text-xs text-gray-500">
                  {kycOverview.overall === 'CLEAR' ? 'Identity, AML / PEP and adverse media are all clear'
                    : kycOverview.overall === 'DEFERRED' ? 'KYC was skipped for now. Complete the wizard when you are ready.'
                    : kycOverview.overall === 'PARTIAL' ? 'Some checks are clear, others still need action'
                    : kycOverview.overall === 'FAILED' ? 'A check failed or a match was confirmed'
                    : 'No checks completed yet'}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { key: 'identity', label: 'Identity (DVS)', value: kycOverview.identity, tab: 'kyc' },
                  { key: 'aml_pep', label: 'AML / PEP', value: kycOverview.aml_pep, tab: 'kyc' },
                  { key: 'adverse_media', label: 'Adverse media', value: kycOverview.adverse_media, tab: 'kyc' },
                ].map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setActiveTab(c.tab)}
                    className="text-left rounded-lg border border-gray-200 p-3 hover:bg-gray-50"
                  >
                    <div className="text-xs text-gray-500 mb-1.5">{c.label}</div>
                    {getStatusBadge(c.value)}
                  </button>
                ))}
              </div>
              <div className="text-xs text-gray-500">
                {kycStatus?.verified_date ? `Identity verified ${formatSydneyDate(new Date(kycStatus.verified_date))}` : 'Identity not yet verified'}
                {kycOverview.last_screened_at ? ` · Last screened ${formatSydneyDate(new Date(kycOverview.last_screened_at))}` : ' · Not screened'}
                {` · ${staffDocsLoading ? '…' : staffDocuments.length} staff upload${staffDocuments.length === 1 ? '' : 's'}`}
              </div>
            </div>
          ) : (
            <div className="text-sm text-gray-500">No KYC data available</div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-psg-teal text-psg-teal'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('kyc')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'kyc'
                  ? 'border-psg-teal text-psg-teal'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              KYC
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('documents')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'documents'
                  ? 'border-psg-teal text-psg-teal'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Documents
            </button>
          </nav>
        </div>

        <div className="p-6">
          {docError && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {docError}
            </div>
          )}
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Employment Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Start Date</div>
                    <div className="flex items-center text-gray-900">
                      <CalendarIcon className="w-5 h-5 mr-2 text-gray-400" />
                      {employee.start_date ? formatSydneyDate(new Date(employee.start_date)) : 'Not set'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">End Date</div>
                    <div className="flex items-center text-gray-900">
                      <CalendarIcon className="w-5 h-5 mr-2 text-gray-400" />
                      {employee.end_date ? formatSydneyDate(new Date(employee.end_date)) : 'Current'}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Created</div>
                    <div className="text-gray-900">
                      {formatSydneyDate(new Date(employee.created_at))}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">Last Updated</div>
                    <div className="text-gray-900">
                      {formatSydneyDate(new Date(employee.updated_at))}
                    </div>
                  </div>
                </div>
              </div>

              {kycOverview && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Compliance Summary</h3>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900">KYC</span>
                      {getStatusBadge(kycOverview.overall)}
                    </div>
                    {[['Identity (DVS)', kycOverview.identity], ['AML / PEP', kycOverview.aml_pep], ['Adverse media', kycOverview.adverse_media]].map(([label, v]) => (
                      <div key={label} className="flex items-center justify-between pl-4">
                        <span className="text-sm text-gray-700">{label}</span>
                        {getStatusBadge(v)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* KYC Tab */}
          {activeTab === 'kyc' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Identity verification (DVS)</h3>
                {kycStatus ? (
                  <div className="space-y-4">
                    <div className="flex flex-wrap gap-2 justify-end">
                      <button
                        type="button"
                        onClick={openKycWizard}
                        className="px-3 py-1.5 text-sm border border-psg-teal text-psg-teal rounded-lg hover:bg-teal-50"
                      >
                        {kycActionLabel()}
                      </button>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <div className="text-sm text-gray-500 mb-1">Status</div>
                          <div>{getStatusBadge(kycStatus.status)}</div>
                        </div>
                        {kycStatus.verified_date && (
                          <div>
                            <div className="text-sm text-gray-500 mb-1">Verification Date</div>
                            <div className="text-gray-900">
                              {formatSydneyDate(new Date(kycStatus.verified_date))}
                            </div>
                          </div>
                        )}
                        {kycStatus.verified_by && (
                          <div>
                            <div className="text-sm text-gray-500 mb-1">Verified By</div>
                            <div className="text-gray-900">{kycStatus.verified_by}</div>
                          </div>
                        )}
                        {kycStatus.documents_count !== undefined && (
                          <div>
                            <div className="text-sm text-gray-500 mb-1">Documents Uploaded</div>
                            <div className="text-gray-900">{kycStatus.documents_count} documents</div>
                          </div>
                        )}
                      </div>
                    </div>

                    {kycStatus.documents && kycStatus.documents.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-3">Uploaded Documents</h4>
                        <div className="space-y-2">
                          {kycStatus.documents.map((doc, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-3">
                              <div className="flex items-center">
                                <DocumentTextIcon className="w-5 h-5 text-gray-400 mr-3" />
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{doc.document_type}</div>
                                  <div className="text-xs text-gray-500">
                                    Uploaded {formatSydneyDate(new Date(doc.uploaded_at))}
                                  </div>
                                </div>
                              </div>
                              <button className="text-sm text-psg-teal hover:text-psg-teal-700">
                                View
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {kycStatus.notes && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Notes</h4>
                        <div className="bg-white border border-gray-200 rounded-lg p-3 text-sm text-gray-700">
                          {kycStatus.notes}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <IdentificationIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 mb-4">No KYC verification records found</p>
                    <button
                      type="button"
                      onClick={openKycWizard}
                      className="px-4 py-2 bg-psg-teal text-white rounded-lg hover:bg-psg-teal-700"
                    >
                      Start KYC wizard
                    </button>
                  </div>
                )}
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">AML / PEP</h3>
                <p className="text-sm text-gray-500 mb-4">PEP, sanctions and watch-list matches. Review any matches in the screening panel.</p>
                <div className="max-w-xl">
                  <EcddAmlPepPanel
                    variant="rail"
                    memberId={`STAFF-${employee.employee_id || employee.id}`}
                    memberName={employee.name}
                    reason="Employee AML/PEP screening"
                    subjectType="staff"
                    onScreened={refreshAmlStatus}
                    onHistoryLoaded={refreshAmlStatus}
                  />
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Adverse media</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Adverse media comes from the same screening run as AML / PEP. Matches are reviewed in the AML / PEP panel above.
                </p>
                <div className="flex items-center gap-3">
                  {getStatusBadge(kycOverview?.adverse_media || 'NOT_SCREENED')}
                  <span className="text-sm text-gray-500">
                    {kycOverview?.adverse_media === 'NOT_SCREENED' || !kycOverview ? 'Run AML / PEP screening to check adverse media.' : ''}
                  </span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Staff documents</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Upload self-declarations, PEP forms, certifications, or other HR/compliance files. Stored separately from patron KYC registry.
                </p>
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="staff-doc-type" className="block text-xs font-medium text-gray-600 mb-1">
                        Document type
                      </label>
                      <select
                        id="staff-doc-type"
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-psg-teal focus:outline-none focus:ring-1 focus:ring-psg-teal"
                        value={docUploadType}
                        onChange={(e) => setDocUploadType(e.target.value)}
                      >
                        {STAFF_DOC_TYPES.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label htmlFor="staff-doc-file" className="block text-xs font-medium text-gray-600 mb-1">
                        File
                      </label>
                      <input
                        id="staff-doc-file"
                        ref={fileRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.txt"
                        className="block w-full text-sm text-gray-600 file:mr-3 file:rounded file:border-0 file:bg-psg-teal file:px-3 file:py-1.5 file:text-white file:hover:bg-psg-teal-700"
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={uploadStaffDocument}
                      disabled={docUploading}
                      className="inline-flex items-center gap-2 rounded-lg bg-psg-teal px-4 py-2 text-sm font-medium text-white hover:bg-psg-teal-700 disabled:opacity-50"
                    >
                      <ArrowUpTrayIcon className="h-4 w-4" />
                      {docUploading ? 'Uploading…' : 'Upload'}
                    </button>
                    <span className="text-xs text-gray-500">
                      Requires <span className="font-mono">admin/user-management:manage</span>. PDF or images up to your server limit.
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-2">On file</h4>
                {staffDocsLoading ? (
                  <p className="text-sm text-gray-500">Loading…</p>
                ) : staffDocuments.length === 0 ? (
                  <p className="text-sm text-gray-500 py-6 text-center bg-gray-50 rounded-lg border border-dashed border-gray-200">
                    No documents uploaded yet.
                  </p>
                ) : (
                  <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
                    {staffDocuments.map((doc) => (
                      <li key={doc.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm">
                        <div className="flex min-w-0 items-center gap-3">
                          <DocumentTextIcon className="h-5 w-5 shrink-0 text-gray-400" />
                          <div className="min-w-0">
                            <div className="font-medium text-gray-900 truncate" title={doc.file_name}>
                              {doc.file_name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatStaffDocType(doc.document_type)}
                              {doc.uploaded_at ? ` · ${formatSydneyDateTime(new Date(doc.uploaded_at))}` : ''}
                              {doc.uploaded_by ? ` · ${doc.uploaded_by}` : ''}
                              {doc.size_bytes != null ? ` · ${(Number(doc.size_bytes) / 1024).toFixed(1)} KB` : ''}
                            </div>
                          </div>
                        </div>
                        <div className="shrink-0 flex items-center gap-2">
                          <button type="button" onClick={() => viewStaffDocument(doc.id, doc.file_name)} className="rounded-md border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-psg-teal hover:bg-gray-50">View</button>
                          <button type="button" onClick={() => downloadStaffDocument(doc.id, doc.file_name)} className="rounded-md border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-psg-teal hover:bg-gray-50">Download</button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <EmployeeKycWizardModal
        open={kycWizardOpen}
        onClose={() => setKycWizardOpen(false)}
        staffId={id}
        employeeName={employee.name}
        employeeId={employee.employee_id || ''}
        onComplete={() => {
          setKycWizardOpen(false)
          loadEmployeeDetails()
        }}
      />

      {deferOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl p-5 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Skip KYC for now?</h3>
            <p className="text-sm text-gray-600">
              The employee record stays active. You can complete identity verification later from this page.
            </p>
            <label className="block text-sm">
              <span className="text-gray-700">Reason (optional)</span>
              <textarea
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                rows={3}
                maxLength={500}
                value={deferReason}
                onChange={(e) => setDeferReason(e.target.value.slice(0, 500))}
                placeholder="e.g. starts next week; will verify on first shift"
              />
            </label>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50"
                onClick={() => setDeferOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deferLoading}
                className="px-4 py-2 text-sm rounded-lg bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-50"
                onClick={handleKycDefer}
              >
                {deferLoading ? 'Saving…' : 'Skip for now'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
